import { useState } from 'react';
import { IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next';
import { selectOptionalUser } from 'features/auth/store/authSelectors';
import { useAppSelector } from 'features/store/hooks';
import { AccountMenu } from './AccountMenu';
import { InitialsAvatar } from './InitialsAvatar';

type AccountMenuButtonProps = {
  /** Avatar edge in px; `InitialsAvatar`'s default applies when omitted. */
  size?: number;
};

/** The signed-in user's avatar button opening the account menu (popover on
 *  desktop, bottom sheet on mobile). Renders nothing when signed out. */
export const AccountMenuButton = ({ size }: AccountMenuButtonProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const user = useAppSelector(selectOptionalUser);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(anchorEl);

  if (!user) {
    return null;
  }

  return (
    <>
      <IconButton
        onClick={event => setAnchorEl(event.currentTarget)}
        aria-label={t('ShellAccountButtonLabel')}
        aria-haspopup="true"
        aria-expanded={menuOpen ? 'true' : undefined}
        sx={{ p: 0, flexShrink: 0 }}
      >
        <InitialsAvatar {...(size !== undefined && { size })} />
      </IconButton>
      <AccountMenu
        variant={isMobile ? 'sheet' : 'popover'}
        placement="down"
        open={menuOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
      />
    </>
  );
};
